// src/pages/ProductionToolkit/components/ProductionPlanning/plannerViewSlice.js
// Slice forecast rows by configured planner view (dispatch weekday(s) + shipping method match)

/** UI / storage: Monday-first options; `value` matches JavaScript Date#getDay() */
export const PLANNER_VIEW_WEEKDAY_OPTIONS = [
    { value: 1, label: 'Monday' },
    { value: 2, label: 'Tuesday' },
    { value: 3, label: 'Wednesday' },
    { value: 4, label: 'Thursday' },
    { value: 5, label: 'Friday' },
    { value: 6, label: 'Saturday' },
    { value: 0, label: 'Sunday' },
];

const WEEKDAY_LABEL_BY_JS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const MONDAY_FIRST_ORDER = PLANNER_VIEW_WEEKDAY_OPTIONS.map((o) => o.value);

/**
 * @param {number} jsDay 0–6 (Date#getDay)
 */
export function plannerWeekdayLabel(jsDay) {
    if (typeof jsDay !== 'number' || jsDay < 0 || jsDay > 6) return '—';
    return WEEKDAY_LABEL_BY_JS[jsDay] || '—';
}

/**
 * @param {string} yyyyMmDd
 * @returns {number|null} JS weekday 0–6
 */
export function getJsWeekdayFromYyyyMmDd(yyyyMmDd) {
    const s = String(yyyyMmDd || '').trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
    const d = new Date(`${s}T12:00:00`);
    if (Number.isNaN(d.getTime())) return null;
    return d.getDay();
}

/**
 * Normalised list of JS weekdays (0–6), Monday-first order, deduped.
 * Prefers `dispatchWeekdays` array; else single `dispatchWeekday`; else legacy `dispatchDate` YYYY-MM-DD.
 * @param {object|null|undefined} view
 * @returns {number[]}
 */
export function resolvePlannerViewWeekdays(view) {
    if (view == null) return [];

    const multi = view.dispatchWeekdays;
    if (Array.isArray(multi) && multi.length > 0) {
        const set = new Set();
        multi.forEach((x) => {
            const n = Number(x);
            if (Number.isInteger(n) && n >= 0 && n <= 6) set.add(n);
        });
        if (set.size > 0) {
            return MONDAY_FIRST_ORDER.filter((v) => set.has(v));
        }
    }

    const w = view.dispatchWeekday;
    if (typeof w === 'number' && w >= 0 && w <= 6) return [w];

    const legacy = String(view.dispatchDate ?? '').trim();
    if (legacy) {
        const d = getJsWeekdayFromYyyyMmDd(legacy);
        return d !== null ? [d] : [];
    }

    return [];
}

/**
 * First weekday (Monday-first list) — legacy helper.
 * @param {object|null|undefined} view
 * @returns {number|null}
 */
export function resolvePlannerViewWeekday(view) {
    const arr = resolvePlannerViewWeekdays(view);
    return arr.length ? arr[0] : null;
}

/** e.g. "Monday, Wednesday, Friday" */
export function formatPlannerViewWeekdaysLabel(view) {
    const days = resolvePlannerViewWeekdays(view);
    if (days.length === 0) return '—';
    return days.map((d) => plannerWeekdayLabel(d)).join(', ');
}

/**
 * Comma-separated shipping phrases: order matches if its method contains **any** phrase (trimmed, case-insensitive).
 * @param {string} raw
 * @returns {string[]} non-empty lowercase needles
 */
export function parsePlannerViewShippingNeedles(raw) {
    return String(raw || '')
        .split(',')
        .map((p) => p.trim().toLowerCase())
        .filter(Boolean);
}

export function isPlannerViewActive(view) {
    const weekdays = resolvePlannerViewWeekdays(view);
    const needles = parsePlannerViewShippingNeedles(view?.shippingMethod ?? '');
    return weekdays.length > 0 && needles.length > 0;
}

/**
 * True when an order’s dispatch weekday + shipping method match a named planner view.
 * `order.dispatchDate` should be YYYY-MM-DD (planner order summaries).
 */
export function orderMatchesPlannerView(order, view) {
    if (!order || !isPlannerViewActive(view)) return false;
    const weekdaySet = new Set(resolvePlannerViewWeekdays(view));
    const needles = parsePlannerViewShippingNeedles(view?.shippingMethod ?? '');
    const dow = getJsWeekdayFromYyyyMmDd(order.dispatchDate);
    if (dow === null || !weekdaySet.has(dow)) return false;
    const shipping = String(order.shippingMethod || order.shipping_method || '')
        .trim()
        .toLowerCase();
    if (!shipping) return false;
    return needles.some((n) => shipping.includes(n));
}

/**
 * Named Setup views that actually appear in the loaded order sample (hide empty chips).
 */
export function filterPlannerViewsPresentInOrders(views, orders) {
    const list = Array.isArray(views) ? views : [];
    const sample = Array.isArray(orders) ? orders : [];
    if (sample.length === 0) return [];
    return list.filter((view) => sample.some((order) => orderMatchesPlannerView(order, view)));
}

/**
 * Rebuild forecast lines: include order lines only when dispatch falls on one of the configured weekdays
 * and shipping method contains **any** comma-separated phrase in `view.shippingMethod` (case-insensitive).
 * Table columns = all folder dispatch dates that fall on those weekdays.
 *
 * @param {object[]} forecast — full planner forecast from OrderDesk processing
 * @param {object|null} view — planner view config from settings
 * @param {string[]} folderDates — original dispatch date columns (YYYY-MM-DD)
 * @returns {{ products: object[], displayDates: string[] }}
 */
export function sliceForecastForPlannerView(forecast, view, folderDates) {
    const dates = Array.isArray(folderDates) ? folderDates : [];

    if (!Array.isArray(forecast) || forecast.length === 0) {
        // Match column weekdays to named planner views even when there are no demand lines yet
        if (!isPlannerViewActive(view)) {
            return { products: [], displayDates: dates };
        }
        const weekdaySet = new Set(resolvePlannerViewWeekdays(view));
        const displayDates = dates.filter((dateStr) => {
            const dow = getJsWeekdayFromYyyyMmDd(dateStr);
            return dow !== null && weekdaySet.has(dow);
        });
        return { products: [], displayDates };
    }

    if (!isPlannerViewActive(view)) {
        return { products: forecast, displayDates: dates };
    }

    const weekdaySet = new Set(resolvePlannerViewWeekdays(view));
    const shippingNeedles = parsePlannerViewShippingNeedles(view.shippingMethod);

    const matchShipping = (sm) => {
        const s = (sm || '').toString().trim().toLowerCase();
        if (shippingNeedles.length === 0) return true;
        return shippingNeedles.some((n) => s.includes(n));
    };

    const displayDates = dates.filter((dateStr) => {
        const dow = getJsWeekdayFromYyyyMmDd(dateStr);
        return dow !== null && weekdaySet.has(dow);
    });

    const products = [];

    for (const product of forecast) {
        const newDailyData = {};
        const newOrderBreakdown = {};
        let any = false;

        for (const dateStr of dates) {
            const dow = getJsWeekdayFromYyyyMmDd(dateStr);
            if (dow === null || !weekdaySet.has(dow)) continue;

            const allEntries = product.orderBreakdown?.[dateStr] || [];
            const entries = allEntries.filter((e) => matchShipping(e.shippingMethod));
            if (entries.length === 0) continue;

            let packing = 0;
            let pending = 0;
            let sticky = 0;
            entries.forEach((e) => {
                const q = Number(e.quantity) || 0;
                if (q <= 0) return;
                if (e.unconfirmedPending) pending += q;
                else if (e.stickyIntegrity) sticky += q;
                else packing += q;
            });
            if (packing <= 0 && pending <= 0 && sticky <= 0) continue;

            newDailyData[dateStr] = { packing, production: packing, pending, sticky };
            newOrderBreakdown[dateStr] = entries;
            any = true;
        }

        if (!any) continue;

        products.push({
            ...product,
            dailyData: newDailyData,
            orderBreakdown: newOrderBreakdown,
        });
    }

    return { products, displayDates };
}

/**
 * True when an order matches **any** of the given active views (OR).
 */
export function orderMatchesAnyPlannerView(order, views) {
    const list = Array.isArray(views) ? views : [];
    return list.some((view) => orderMatchesPlannerView(order, view));
}

/**
 * Multi-select Views: OR match on order lines; date columns = union of selected weekdays.
 * Empty / inactive selection behaves like All (full forecast).
 *
 * @param {object[]} forecast
 * @param {object[]} views — selected Setup views (0 = All)
 * @param {string[]} folderDates
 * @returns {{ products: object[], displayDates: string[] }}
 */
export function sliceForecastForPlannerViews(forecast, views, folderDates) {
    const dates = Array.isArray(folderDates) ? folderDates : [];
    const active = (Array.isArray(views) ? views : []).filter((v) => isPlannerViewActive(v));

    if (active.length === 0) {
        return sliceForecastForPlannerView(forecast, null, dates);
    }
    if (active.length === 1) {
        return sliceForecastForPlannerView(forecast, active[0], dates);
    }

    const weekdayUnion = new Set();
    active.forEach((view) => {
        resolvePlannerViewWeekdays(view).forEach((d) => weekdayUnion.add(d));
    });

    const displayDates = dates.filter((dateStr) => {
        const dow = getJsWeekdayFromYyyyMmDd(dateStr);
        return dow !== null && weekdayUnion.has(dow);
    });

    if (!Array.isArray(forecast) || forecast.length === 0) {
        return { products: [], displayDates };
    }

    const entryMatches = (dateStr, entry) => {
        const dow = getJsWeekdayFromYyyyMmDd(dateStr);
        if (dow === null) return false;
        return active.some((view) => {
            const weekdaySet = new Set(resolvePlannerViewWeekdays(view));
            if (!weekdaySet.has(dow)) return false;
            const needles = parsePlannerViewShippingNeedles(view.shippingMethod);
            const shipping = String(entry?.shippingMethod || entry?.shipping_method || '')
                .trim()
                .toLowerCase();
            if (!shipping || needles.length === 0) return false;
            return needles.some((n) => shipping.includes(n));
        });
    };

    const products = [];

    for (const product of forecast) {
        const newDailyData = {};
        const newOrderBreakdown = {};
        let any = false;

        for (const dateStr of dates) {
            const dow = getJsWeekdayFromYyyyMmDd(dateStr);
            if (dow === null || !weekdayUnion.has(dow)) continue;

            const allEntries = product.orderBreakdown?.[dateStr] || [];
            const entries = allEntries.filter((e) => entryMatches(dateStr, e));
            if (entries.length === 0) continue;

            let packing = 0;
            let pending = 0;
            let sticky = 0;
            entries.forEach((e) => {
                const q = Number(e.quantity) || 0;
                if (q <= 0) return;
                if (e.unconfirmedPending) pending += q;
                else if (e.stickyIntegrity) sticky += q;
                else packing += q;
            });
            if (packing <= 0 && pending <= 0 && sticky <= 0) continue;

            newDailyData[dateStr] = { packing, production: packing, pending, sticky };
            newOrderBreakdown[dateStr] = entries;
            any = true;
        }

        if (!any) continue;

        products.push({
            ...product,
            dailyData: newDailyData,
            orderBreakdown: newOrderBreakdown,
        });
    }

    return { products, displayDates };
}
